import { CellContext, Column, Row } from '@tanstack/react-table'

import CharacterClassIcon from '../../components/CharacterClassIcon'
import { genericSorting } from './util'
import CharacterRoleIcon from '../../components/CharacterRoleIcon'
import {
  applicationStatusColors,
  applicationStatuses,
  applicationStatusLabels,
} from 'app/frontend/types/custom/ApplicationStatus'
import CharacterExternalIconLink from '../../components/CharacterExternalIconLink'
import dayjs from 'dayjs'
import { imageUrl } from '../../helpers'
import { capitalize } from 'lodash'
import { ColumnFilter } from '@/components/ui/column-filter'
import { applicationRoles } from 'app/frontend/types/custom/ApplicationRole'
import {
  characterClasses,
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import { BellPlus } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { isUnread } from '../../pages/TeamDashboard/subpages/TeamApplicants/utils'
import { usePageInfo, useUserApplicationSummary } from '../queries'
import BannerImage from '../../components/BannerImage'
import NumberIndicator from '../../components/NumberIndicator'
import { cn } from '@/lib/utils'

export const characterName = (props: { withFilters: boolean }) => ({
  accessorFn: (row: wowaudit.Application) =>
    row.characterReference?.name ??
    row.legacyCharacterName ??
    row.userReference?.obfuscatedBattletag,
  id: 'characterName',
  size: 185,
  filterFn: (
    row: Row<wowaudit.Application>,
    _id: string,
    filterValue: string[],
  ) => {
    if (!filterValue || filterValue.length === 0) return true
    const characterClass =
      row.original.characterReference?.characterClass ??
      row.original.legacyCharacterClass
    return characterClass ? filterValue.includes(characterClass) : false
  },
  header: ({ column }: { column: Column<wowaudit.Application, unknown> }) => (
    <div className='flex items-center space-x-2'>
      <span className='cursor-pointer' onClick={() => column.toggleSorting()}>
        Character
      </span>
      {props.withFilters && (
        <ColumnFilter
          column={column}
          options={characterClasses.map((characterClass) => ({
            label: characterClass,
            value: characterClass,
          }))}
        />
      )}
    </div>
  ),
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    const {
      data: { user },
    } = usePageInfo()
    const application = info.row.original
    const character = application.characterReference

    if (!character && !application.legacyCharacterPath) {
      return (
        <span className='flex items-center gap-2'>
          <img src={imageUrl('bnet-logo-blue.svg')} className='w-6' />
          {info.row.original.userReference
            ? info.row.original.userReference.obfuscatedBattletag
            : user?.obfuscatedBattletag}
        </span>
      )
    }

    return (
      <div
        className={cn(
          'flex items-center gap-2',
          classColours[
            character?.characterClass ??
              application.legacyCharacterClass ??
              'Priest'
          ].text,
        )}
      >
        <CharacterClassIcon
          characterClass={
            character?.characterClass ?? application.legacyCharacterClass
          }
        />

        <span>{character?.name ?? application.legacyCharacterName}</span>
      </div>
    )
  },
})

export const role = (props: { withFilters: boolean }) => ({
  accessorKey: 'role',
  id: 'role',
  size: 130,
  filterFn: (row: Row<wowaudit.Application>, id: string, value: string[]) => {
    if (!value || value.length === 0) return true
    return value.includes(row.getValue(id))
  },
  header: ({ column }: { column: Column<wowaudit.Application, unknown> }) => (
    <div className='flex items-center space-x-2'>
      <span className='cursor-pointer' onClick={() => column.toggleSorting()}>
        Role
      </span>
      {props.withFilters && (
        <ColumnFilter
          column={column}
          options={applicationRoles.map((role) => ({
            label: capitalize(role),
            value: role,
          }))}
        />
      )}
    </div>
  ),
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    return (
      <span className='flex items-center gap-2'>
        <CharacterRoleIcon role={info.row.original.role} />
        {capitalize(info.row.original.role)}
      </span>
    )
  },
})

export const status = (props: { withFilters: boolean }) => ({
  accessorKey: 'status',
  id: 'status',
  size: 130,
  filterFn: (row: Row<wowaudit.Application>, id: string, value: string[]) => {
    if (!value || value.length === 0) return true
    return value.includes(row.getValue(id))
  },
  header: ({ column }: { column: Column<wowaudit.Application, unknown> }) => (
    <div className='flex items-center space-x-2'>
      <span className='cursor-pointer' onClick={() => column.toggleSorting()}>
        Status
      </span>
      {props.withFilters && (
        <ColumnFilter
          column={column}
          options={applicationStatuses
            .filter(
              (status) =>
                !['draft', 'submitted', 'under_review'].includes(status),
            )
            .map((status) => ({
              label: applicationStatusLabels[status],
              value: status,
            }))}
        />
      )}
    </div>
  ),
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    const statusValue = info.row.original.status
    const { data: applicationSummary } = useUserApplicationSummary()

    return (
      <span
        className={`flex items-center gap-4 ${applicationStatusColors[statusValue].text}`}
      >
        {applicationStatusLabels[statusValue]}
        {(applicationSummary ?? {})?.[info.row.original.id] > 0 ? (
          <Tooltip>
            <TooltipTrigger>
              <NumberIndicator
                number={applicationSummary?.[info.row.original.id] ?? 0}
              />
            </TooltipTrigger>
            <TooltipContent side='left'>
              {applicationSummary?.[info.row.original.id] ?? 0} new messages
            </TooltipContent>
          </Tooltip>
        ) : null}
      </span>
    )
  },
})

export const date = {
  accessorKey: 'createdAt',
  id: 'date',
  size: 120,
  ...genericSorting<wowaudit.Application>('datetime', 'Date'),
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    return (
      <span className='flex items-center gap-2'>
        <span>{dayjs(info.row.original.createdAt).format('D MMM YYYY')} </span>
        {isUnread(info.row.original) ? (
          <Tooltip>
            <TooltipTrigger className='text-success-foreground'>
              <BellPlus size={16} />
            </TooltipTrigger>
            <TooltipContent>New!</TooltipContent>
          </Tooltip>
        ) : null}
      </span>
    )
  },
}

export const formatAge = (createdAt: string) => {
  const now = dayjs()
  const created = dayjs(createdAt)
  const diffInHours = now.diff(created, 'hour')
  const diffInDays = now.diff(created, 'day')

  if (diffInHours < 24) return `${diffInHours}h`
  return (
    <span
      className={
        diffInDays > 5
          ? diffInDays > 21
            ? 'text-destructive-foreground'
            : 'text-warning-foreground'
          : 'text-success-foreground'
      }
    >
      {diffInDays}d
    </span>
  )
}

export const age = {
  accessorKey: 'createdAt',
  id: 'age',
  size: 50,
  ...genericSorting<wowaudit.Application>('alphanumeric', 'Age', true),
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    return (
      <Tooltip>
        <TooltipTrigger>
          {formatAge(info.row.original.createdAt)}
        </TooltipTrigger>
        <TooltipContent>
          Submitted on{' '}
          {dayjs(info.row.original.createdAt).format('D MMM YYYY, HH:mm')}
        </TooltipContent>
      </Tooltip>
    )
  },
}

export const team = {
  header: 'Team',
  accessorKey: 'team',
  id: 'team',
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    return info.row.original.teamReference ? (
      <div className='flex items-center gap-1'>
        <BannerImage
          faction={info.row.original.teamReference.ownerReference?.faction}
          fileName={
            info.row.original.teamReference.ownerReference?.bannerFilename
          }
          size='custom'
          className='w-6 h-6 min-w-6 min-h-6'
        />
        {info.row.original.teamReference.ownerReference?.name}{' '}
        <span className='text-muted-foreground text-xs'>
          ({info.row.original.teamReference.name})
        </span>
      </div>
    ) : (
      <div className='text-muted-foreground italic'>Unknown</div>
    )
  },
}

export const links = {
  header: 'Links',
  accessorKey: 'links',
  id: 'links',
  size: 120,
  cell: (info: CellContext<wowaudit.Application, unknown>) => {
    return (
      <div className='flex gap-2 items-center'>
        <CharacterExternalIconLink
          type='blizzard'
          backendUrl={info.row.original.characterReference?.blizzardUrl}
          realmName={info.row.original.legacyCharacterRealm?.slug}
          region={info.row.original.legacyCharacterRealm?.region}
          entityName={info.row.original.legacyCharacterName}
        />
        <CharacterExternalIconLink
          type='wcl'
          className='w-5 min-w-5'
          backendUrl={info.row.original.characterReference?.wclUrl}
          realmName={info.row.original.legacyCharacterRealm?.slug}
          region={info.row.original.legacyCharacterRealm?.region}
          entityName={info.row.original.legacyCharacterName}
        />
        <CharacterExternalIconLink
          type='raiderio'
          backendUrl={info.row.original.characterReference?.raiderioUrl}
          realmName={info.row.original.legacyCharacterRealm?.slug}
          region={info.row.original.legacyCharacterRealm?.region}
          entityName={info.row.original.legacyCharacterName}
        />
      </div>
    )
  },
}
